import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import StatusIconCDS from '@sharedCR/coreDesignSystem/StatusIconCDS';
import PropTypes from 'prop-types';

import { AiFormStatus } from 'constants/aiProcessRuns';
import { StatusEnum } from 'constants/status';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  loadExecutionFormAction,
  selectFormByExecution,
  selectHasFormEntry,
} from 'store/aiProcessRuns';

import { formatExactTime } from '../../../AiActivity.utils';

import {
  countFieldsNeedingAttention,
  countFilledFields,
  countSchemaFields,
} from './formReview.utils';
import {
  DRAFTING_STATUS_LABELS,
  FORM_REVIEW_LABELS,
  FORM_STATUS_LABELS,
  formatFieldProgress,
  formatNeedsAttention,
  FormFillState,
  FormReviewTestIds,
} from './FormReviewCard.constants';
import {
  cardStyles,
  getProgressStripFillStyles,
  headerStyles,
  headerTitleRowStyles,
  liveFillRowStyles,
  progressBlockStyles,
  progressLabelRowStyles,
  progressStripTrackStyles,
  summaryRowStyles,
  transitionRowStyles,
} from './FormReviewCard.styles';
import FormReviewSurface from './FormReviewSurface';

const FormReviewCard = ({ executionId, fillState = FormFillState.FILLING }) => {
  const dispatch = useDispatch();
  const form = useSelector(selectFormByExecution(executionId));
  const hasEntry = useSelector(selectHasFormEntry(executionId));
  const [isSurfaceOpen, setIsSurfaceOpen] = useState(false);

  const needsAttentionCount = useMemo(() => countFieldsNeedingAttention(form), [form]);
  const filledCount = useMemo(() => countFilledFields(form), [form]);
  const totalCount = useMemo(() => countSchemaFields(form), [form]);

  // Dual-source: a live form_field_set/form_status_changed may have already
  // seeded this form. Only fetch over REST when the drawer opens and the slice
  // has no entry yet; the live path otherwise wins and short-circuits this.
  useEffectSafeStrictMode(() => {
    if (!hasEntry && executionId) {
      dispatch(loadExecutionFormAction({ executionId }));
    }
  }, [dispatch, executionId, hasEntry]);

  const onOpenSurface = useCallback(() => setIsSurfaceOpen(true), []);

  const onCloseSurface = useCallback(() => setIsSurfaceOpen(false), []);

  if (!form) {
    return null;
  }

  const isDrafting = form.status === AiFormStatus.DRAFTING;
  const statusLabel = isDrafting
    ? DRAFTING_STATUS_LABELS[fillState]
    : (FORM_STATUS_LABELS[form.status] ?? form.status);
  const showLiveFill = isDrafting && fillState === FormFillState.FILLING;
  const showPaused = isDrafting && fillState === FormFillState.PAUSED;
  const showAwaitingReview = isDrafting && fillState === FormFillState.DONE;
  const hasNeedsAttention = needsAttentionCount > 0;
  const progressRatio = totalCount > 0 ? filledCount / totalCount : 0;

  return (
    <>
      <Box data-testid={FormReviewTestIds.CARD} sx={cardStyles}>
        <Box sx={headerStyles}>
          <Box sx={headerTitleRowStyles}>
            <Typography variant="bold14">{FORM_REVIEW_LABELS.CARD_TITLE}</Typography>
            <Typography data-testid={FormReviewTestIds.STATUS_PILL} variant="bold12">
              {statusLabel}
            </Typography>
          </Box>
          {showLiveFill ? (
            <Box data-testid={FormReviewTestIds.LIVE_FILL} sx={liveFillRowStyles}>
              <StatusIconCDS size={14} type={StatusEnum.IN_PROGRESS} />
              <Typography variant="regular12">{FORM_REVIEW_LABELS.LIVE_FILL}</Typography>
            </Box>
          ) : null}
          {showPaused ? (
            <Box data-testid={FormReviewTestIds.PAUSED} sx={liveFillRowStyles}>
              <StatusIconCDS size={14} type={StatusEnum.WARNING} />
              <Typography variant="regular12">{FORM_REVIEW_LABELS.PAUSED}</Typography>
            </Box>
          ) : null}
          {showAwaitingReview ? (
            <Box data-testid={FormReviewTestIds.AWAITING_REVIEW} sx={liveFillRowStyles}>
              <StatusIconCDS size={14} type={StatusEnum.INFO} />
              <Typography variant="regular12">{FORM_REVIEW_LABELS.AWAITING_REVIEW}</Typography>
            </Box>
          ) : null}
          <Box data-testid={FormReviewTestIds.NEEDS_ATTENTION} sx={summaryRowStyles}>
            <StatusIconCDS
              size={14}
              type={hasNeedsAttention ? StatusEnum.WARNING : StatusEnum.SUCCESS}
            />
            <Typography variant="regular12">
              {hasNeedsAttention
                ? formatNeedsAttention(needsAttentionCount)
                : FORM_REVIEW_LABELS.ALL_CLEAR}
            </Typography>
          </Box>
          {totalCount > 0 ? (
            <Box sx={progressBlockStyles}>
              <Box sx={progressLabelRowStyles}>
                <Typography variant="regular12">
                  {formatFieldProgress(filledCount, totalCount)}
                </Typography>
              </Box>
              <Box sx={progressStripTrackStyles}>
                <Box
                  data-testid={FormReviewTestIds.PROGRESS_STRIP}
                  sx={getProgressStripFillStyles(progressRatio)}
                />
              </Box>
            </Box>
          ) : null}
          {form.schemaVersionMatches === false ? (
            <Typography
              color="warning"
              data-testid={FormReviewTestIds.SCHEMA_MISMATCH}
              variant="regular12"
            >
              {FORM_REVIEW_LABELS.SCHEMA_MISMATCH}
            </Typography>
          ) : null}
          {form.reviewedByUserName ? (
            <Typography data-testid={FormReviewTestIds.REVIEWED_BY} variant="regular12">
              {`${FORM_REVIEW_LABELS.REVIEWED_BY} ${form.reviewedByUserName}`}
              {form.reviewedAt ? ` · ${formatExactTime(form.reviewedAt)}` : ''}
            </Typography>
          ) : null}
        </Box>
        <Box sx={transitionRowStyles}>
          <Button
            data-testid={FormReviewTestIds.OPEN_SURFACE_BUTTON}
            size="small"
            onClick={onOpenSurface}
          >
            {FORM_REVIEW_LABELS.OPEN_SURFACE}
          </Button>
        </Box>
      </Box>
      <FormReviewSurface form={form} isOpen={isSurfaceOpen} onClose={onCloseSurface} />
    </>
  );
};

FormReviewCard.propTypes = {
  executionId: PropTypes.string.isRequired,
  fillState: PropTypes.oneOf(Object.values(FormFillState)),
};

export default memo(FormReviewCard);
